import { Button } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import { trackCommunityWelcomeDismissed } from 'src/Services/Mixpanel/events';

import styles from './WelcomeCard.module.scss';

const STORAGE_KEY_PREFIX = 'community.welcome.dismissed.';

const isDismissed = (userId: string) => {
  try {
    return window.localStorage.getItem(`${STORAGE_KEY_PREFIX}${userId}`) === 'true';
  } catch {
    // Private browsing may forbid localStorage; treat as not dismissed.
    return false;
  }
};

/**
 * First-visit banner that explains why "Entre nous" is not another WhatsApp
 * group. Dismissed per-user via localStorage so a shared computer still hides
 * the card for the person who acknowledged it.
 */
const WelcomeCard = () => {
  const { t } = useTranslation();
  const { data: user } = useFetchUser();
  const [dismissed, setDismissed] = useState(() => (user ? isDismissed(user.id) : false));

  if (!user) return null;
  if (dismissed) return null;
  if (isDismissed(user.id)) return null;

  const onDismiss = () => {
    try {
      window.localStorage.setItem(`${STORAGE_KEY_PREFIX}${user.id}`, 'true');
    } catch {
      // Fail silently: even without persistence the card disappears for this session.
    }
    trackCommunityWelcomeDismissed();
    setDismissed(true);
  };

  return (
    <div className={styles.container}>
      <div className={styles.text}>
        <span className={styles.title}>
          {t('community.welcome.title', 'Pourquoi ici plutôt que sur WhatsApp ?')}
        </span>
        <span className={styles.body}>
          {t(
            'community.welcome.body',
            'Un message WhatsApp disparaît sous le suivant. Ici, chaque sujet garde son fil, chaque photo son album, et la famille retrouve en deux clics la discussion sur la toiture — cette année ou dans dix ans.'
          )}
        </span>
      </div>
      <Button variant="outline" onClick={onDismiss} className={styles.button}>
        {t('community.welcome.dismiss', 'Compris')}
      </Button>
    </div>
  );
};

export default WelcomeCard;
